import React, { useState, useRef, useEffect, useCallback } from 'react';

const ZoomableContainer = ({
  children,
  minZoom = 0.5,
  maxZoom = 3,
  initialZoom = 1,
  zoomSensitivity = 0.001,
  className = ""
}) => {
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({
    scale: initialZoom,
    translateX: 0,
    translateY: 0
  });
  // Refs pour l'état impératif du drag (évite le stale closure avec addEventListener)
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false); // true si le curseur a bougé significativement pendant le drag
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastPinchDistRef = useRef(0);
  const [cursorStyle, setCursorStyle] = useState('grab');
  const DRAG_THRESHOLD = 5; // pixels minimum pour considérer un drag (pas un clic)

  const getPinchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getPinchCenter = (touches) => ({
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2
  });

  const constrainTranslation = useCallback((translateX, translateY, scale) => {
    if (!containerRef.current) return { translateX, translateY };

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const svgElement = container.querySelector('svg');
    if (!svgElement) return { translateX, translateY };

    const svgRect = svgElement.getBoundingClientRect();
    let contentWidth = svgRect.width;
    let contentHeight = svgRect.height;

    if (svgElement.style.width?.includes('vw')) {
      contentWidth = (parseFloat(svgElement.style.width) / 100) * window.innerWidth;
    }
    if (svgElement.style.minHeight?.includes('vh')) {
      contentHeight = Math.max(contentHeight, (parseFloat(svgElement.style.minHeight) / 100) * window.innerHeight);
    }

    const scaledContentWidth = contentWidth * scale;
    const scaledContentHeight = contentHeight * scale;

    // Toujours permettre un minimum de déplacement (15% du conteneur)
    const minPanX = containerRect.width * 0.15;
    const minPanY = containerRect.height * 0.15;

    let maxTranslateX = minPanX;
    let maxTranslateY = minPanY;

    if (scaledContentWidth > containerRect.width) {
      maxTranslateX = Math.max(minPanX, (scaledContentWidth - containerRect.width) / 2);
    }
    if (scaledContentHeight > containerRect.height) {
      maxTranslateY = Math.max(minPanY, (scaledContentHeight - containerRect.height) / 2);
    }

    return {
      translateX: Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX)),
      translateY: Math.max(-maxTranslateY, Math.min(maxTranslateY, translateY))
    };
  }, []);

  const zoomToPoint = useCallback((clientX, clientY, deltaScale) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const containerCenterX = rect.left + rect.width / 2;
    const containerCenterY = rect.top + rect.height / 2;

    setTransform(prev => {
      const newScale = Math.max(minZoom, Math.min(maxZoom, prev.scale + deltaScale));
      if (newScale === prev.scale) return prev;

      const relativeX = clientX - containerCenterX;
      const relativeY = clientY - containerCenterY;
      const scaleFactor = newScale / prev.scale;
      const newTranslateX = prev.translateX + relativeX * (1 - scaleFactor);
      const newTranslateY = prev.translateY + relativeY * (1 - scaleFactor);
      const constrained = constrainTranslation(newTranslateX, newTranslateY, newScale);

      return {
        scale: newScale,
        translateX: constrained.translateX,
        translateY: constrained.translateY
      };
    });
  }, [minZoom, maxZoom, constrainTranslation]);

  // Attacher tous les événements une seule fois (pas de dépendances sur l'état du drag)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const deltaScale = -e.deltaY * zoomSensitivity;
      zoomToPoint(e.clientX, e.clientY, deltaScale);
    };

    const handleMouseDown = (e) => {
      e.preventDefault();
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      setCursorStyle('grabbing');
    };

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;

      const deltaX = e.clientX - lastPointerRef.current.x;
      const deltaY = e.clientY - lastPointerRef.current.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };

      // Détecter si le déplacement est suffisant pour être un drag
      const totalDx = e.clientX - dragStartRef.current.x;
      const totalDy = e.clientY - dragStartRef.current.y;
      if (Math.abs(totalDx) > DRAG_THRESHOLD || Math.abs(totalDy) > DRAG_THRESHOLD) {
        hasDraggedRef.current = true;
      }

      setTransform(prev => {
        const constrained = constrainTranslation(
          prev.translateX + deltaX,
          prev.translateY + deltaY,
          prev.scale
        );
        return { ...prev, ...constrained };
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      lastPinchDistRef.current = 0;
      setCursorStyle('grab');
    };

    // Bloquer le click sur les enfants (hexagones) si on vient de drag
    const handleClick = (e) => {
      if (hasDraggedRef.current) {
        e.stopPropagation();
        e.preventDefault();
        hasDraggedRef.current = false;
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
      if (e.touches.length === 2) {
        lastPinchDistRef.current = getPinchDistance(e.touches);
        isDraggingRef.current = false;
      } else if (e.touches.length === 1) {
        isDraggingRef.current = true;
        lastPointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        const distance = getPinchDistance(e.touches);
        const center = getPinchCenter(e.touches);
        if (lastPinchDistRef.current > 0) {
          const deltaScale = (distance - lastPinchDistRef.current) * 0.01;
          zoomToPoint(center.x, center.y, deltaScale);
        }
        lastPinchDistRef.current = distance;
      } else if (e.touches.length === 1 && isDraggingRef.current) {
        const deltaX = e.touches[0].clientX - lastPointerRef.current.x;
        const deltaY = e.touches[0].clientY - lastPointerRef.current.y;
        lastPointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

        setTransform(prev => {
          const constrained = constrainTranslation(
            prev.translateX + deltaX,
            prev.translateY + deltaY,
            prev.scale
          );
          return { ...prev, ...constrained };
        });
      }
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      lastPinchDistRef.current = 0;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('click', handleClick, true); // capture phase pour intercepter avant les enfants
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('click', handleClick, true);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [zoomSensitivity, zoomToPoint, constrainTranslation]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        cursor: cursorStyle,
        touchAction: 'none',
        userSelect: 'none'
      }}
    >
      <div
        style={{
          transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
          transformOrigin: 'center center',
          transition: isDraggingRef.current ? 'none' : 'transform 0.1s ease-out',
          width: '100%',
          height: '100%'
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ZoomableContainer;
